import React, { useMemo, useCallback, memo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartSeries, Num } from '../../types/performance';
import { fmtPct, fmtDay, fmtDayShort, fmtRatio } from '../../utils/performanceFormat';
import '../../styles/features/portfolio.css';

// Trader theme: series are told apart by brightness + dash, not hue (hue is
// reserved for meaning). Your portfolio = t1 solid, stocks-only = t2 dashed,
// the benchmark = t3 dashed. Hex values mirror the --tk-* tokens because
// Recharts writes them into SVG attributes.
const TK = {
  t1: '#e7e8ea',
  t2: '#a2a7ae',
  t3: '#6f747c',
  line: 'rgba(255, 255, 255, 0.08)',
  mono: "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, monospace",
};

interface PortfolioPerformanceChartProps {
  data: ChartSeries;
  sharpe: Num;
  benchmarkSharpe: Num;
}

/**
 * Cumulative time-weighted returns over the selected window, as served by
 * gold (gold_history_read.chart_series): portfolio, shares only and SPY total
 * return, each chained from the window's first session. The browser plots;
 * it does not rebase, cut or compute.
 */
const PortfolioPerformanceChart: React.FC<PortfolioPerformanceChartProps> = ({ data, sharpe, benchmarkSharpe }) => {
  const hasStocksOnly = data.stock_only.some(v => v != null);
  const benchmarkLabel = data.benchmark_symbol === 'SPY' ? 'S&P 500 (total return)' : data.benchmark_symbol;

  const points = useMemo(() => data.dates.map((date, i) => ({
    date,
    // the time axis needs a number; the date string itself is a New York session
    x: Date.parse(`${date}T12:00:00Z`),
    portfolio: data.portfolio[i],
    stocksOnly: data.stock_only[i],
    benchmark: data.benchmark[i],
  })), [data]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const p = payload[0].payload;
      return (
        <div className="perf-tip">
          <p className="perf-tip-d">{fmtDay(p.date)}</p>
          <p className="perf-tip-r" style={{ color: TK.t1 }}>
            Portfolio <b>{fmtPct(p.portfolio)}</b>
          </p>
          {hasStocksOnly && (
            <p className="perf-tip-r">
              Stocks Only <b>{fmtPct(p.stocksOnly)}</b>
            </p>
          )}
          <p className="perf-tip-r" style={{ color: TK.t3 }}>
            {benchmarkLabel} <b>{fmtPct(p.benchmark)}</b>
          </p>
        </div>
      );
    }
    return null;
  };

  const formatYAxis = useCallback((value: number) => fmtPct(value, 0), []);
  const formatXAxis = useCallback((x: number) => fmtDayShort(new Date(x).toISOString().slice(0, 10)), []);
  const axisTick = { fill: TK.t3, fontSize: 11, fontFamily: TK.mono };

  return (
    <div className="app-performance-charts">
      <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <div className="perf-chart-bar">
          <span className="perf-chart-bar-l">Compared with {benchmarkLabel}</span>
          <div className="perf-sharpe">
            <span className="perf-sharpe-h">Sharpe</span>
            <div className="perf-sharpe-c">
              <span>Portfolio</span>
              <b style={{ color: TK.t1 }}>{fmtRatio(sharpe, 2, '')}</b>
            </div>
            <div className="perf-sharpe-c">
              <span>{data.benchmark_symbol}</span>
              <b style={{ color: TK.t3 }}>{fmtRatio(benchmarkSharpe, 2, '')}</b>
            </div>
          </div>
        </div>

        <div className="perf-chart">
          {points.length === 0 ? (
            <div className="perf-chart-empty">No data available for selected period</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} key={`chart-${points.length}-${data.dates[0]}`}>
                <CartesianGrid stroke={TK.line} vertical={false} />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={formatXAxis}
                  stroke={TK.line}
                  tick={axisTick}
                  tickLine={false}
                />
                <YAxis tickFormatter={formatYAxis} stroke={TK.line} tick={axisTick} tickLine={false} width={48} />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255, 255, 255, 0.16)' }} />
                <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '11.5px', color: TK.t2 }} iconType="plainline" />
                <Area
                  type="monotone"
                  dataKey="benchmark"
                  stroke={TK.t3}
                  fill="none"
                  strokeWidth={1.5}
                  strokeDasharray="2 3"
                  name={benchmarkLabel}
                  connectNulls={false}
                  activeDot={{ r: 3, strokeWidth: 0, fill: TK.t3 }}
                />
                {hasStocksOnly && (
                  <Area
                    type="monotone"
                    dataKey="stocksOnly"
                    stroke={TK.t2}
                    fill="none"
                    strokeWidth={1.5}
                    strokeDasharray="6 3"
                    name="Stocks Only"
                    connectNulls={false}
                    activeDot={{ r: 3, strokeWidth: 0, fill: TK.t2 }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="portfolio"
                  stroke={TK.t1}
                  fill="none"
                  strokeWidth={1.75}
                  name="Your Portfolio"
                  connectNulls={false}
                  activeDot={{ r: 3.5, strokeWidth: 0, fill: TK.t1 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(PortfolioPerformanceChart);
